// src/components/battle/BattleResultSection.jsx
import React, { useMemo } from "react";

// winStatus : 0 = 승, 1 = 패
function splitWinnerLoser(resultList) {
  if (!Array.isArray(resultList)) return { winners: [], losers: [] };
  return {
    winners: resultList.filter((r) => r.winStatus === 0),
    losers: resultList.filter((r) => r.winStatus === 1),
  };
}

export default function BattleResultSection({ mode, resultList, meUserId }) {
  const { winners, losers } = useMemo(
    () => splitWinnerLoser(resultList),
    [resultList]
  );

  const isSolo = Number(mode) === 0; // 0: 개인전, 1: 팀전

  if (!resultList || resultList.length === 0) {
    return (
      <section className="mt-4 mb-24 rounded-2xl bg-white/90 backdrop-blur shadow-sm px-5 py-4">
        <p className="text-sm text-slate-400">아직 결과가 없습니다.</p>
      </section>
    );
  }

  return (
     <section className="mt-3 mb-24 rounded-2xl bg-white/90 backdrop-blur shadow-sm px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-900">대결 결과</h2>
        <span className="text-[11px] text-slate-400">
          {isSolo ? "개인전" : "팀전"} · 승리 EXP +300
        </span>
      </div>

      {/* 개인전 : 승 1명, 패 1명 카드로 분리 */}
      {isSolo ? (
        <div className="space-y-3">
          {winners[0] && (
            <ResultRow
              label="승리"
              type="win"
              runner={winners[0]}
              meUserId={meUserId}
            />
          )}
          {losers[0] && (
            <ResultRow
              label="패배"
              type="lose"
              runner={losers[0]}
              meUserId={meUserId}
            />
          )}
        </div>
      ) : (
        // 팀전 : 승리 팀 / 패배 팀 전체 리스트
        <div className="space-y-4">
          <TeamBlock
            title="승리 팀"
            type="win"
            members={winners}
            meUserId={meUserId}
          />
          <TeamBlock
            title="패배 팀"
            type="lose"
            members={losers}
            meUserId={meUserId}
          />
        </div>
      )}
    </section>
  );
}

// ======================= 공통 한 줄 카드 =======================

function ResultRow({ label, type, runner, meUserId }) {
  const isWin = type === "win";
  const isMe = runner.userId === meUserId;

  return (
    <div
      className={`flex items-center justify-between rounded-2xl px-4 py-3 border text-sm
      ${
        isWin
          ? "border-emerald-300 bg-emerald-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* 승 / 패 배지 */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold 
          ${isWin ? "bg-emerald-400 text-white" : "bg-rose-400 text-white"}`}
        >
          {isWin ? "승" : "패"}
        </div>

        {/* 닉네임 + 나 표시 */}
        <div className="flex flex-col">
          <span className="font-semibold text-slate-900">
            {runner.nickname}
            {isMe && (
              <span className="ml-1 text-[11px] text-emerald-500">(나)</span>
            )}
          </span>
          {runner.finishTime && (
            <span className="text-[11px] text-slate-400">
              완료시간 : {runner.finishTime}
            </span>
          )}
        </div>
      </div>

      {/* EXP 표시 */}
      <div className="text-right text-[11px]">
        <div
          className={`inline-flex px-2 py-[3px] rounded-full font-semibold
          ${
            isWin
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          EXP {isWin ? "+300" : "+0"}
        </div>
      </div>
    </div>
  );
}

// ======================= 팀전 블록 =======================

function TeamBlock({ title, type, members, meUserId }) {
  const isWin = type === "win";

  if (!members || members.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`text-xs font-semibold ${
            isWin ? "text-emerald-600" : "text-rose-500"
          }`}
        >
          {title}
        </span>
        <span className="text-[10px] text-slate-400">
          {members.length}명 참가
        </span>
      </div>

      <div className="space-y-2">
        {members.map((m) => (
          <ResultRow
            key={m.userId}
            label={isWin ? "승리" : "패배"}
            type={type}
            runner={m}
            meUserId={meUserId}
          />
        ))}
      </div>
    </div>
  );
}
